<template>
  <div class="suppliers-wrapper">

    <!-- Notifications -->
    <div class="notifications-wrapper">
      <div v-for="(n, i) in notifications" :key="i" :class="['notification', n.type]">
        {{ n.message }}
      </div>
    </div>

    <!-- Toolbar -->
    <div class="toolbar animated fadeInDown">
      <div class="left">
        <button @click="addSupplier" class="btn-gradient"><i class="fas fa-plus"></i> Add Supplier</button>
        <button @click="refreshSuppliers" class="btn-refresh"><i class="fas fa-sync-alt"></i> Refresh</button>
        <button @click="exportPDF" class="btn-pdf" :disabled="filteredSuppliers.length === 0">
          <i class="fas fa-file-pdf"></i> Export PDF
        </button>
        <button @click="exportExcel" class="btn-excel" :disabled="filteredSuppliers.length === 0">
          <i class="fas fa-file-excel"></i> Export Excel
        </button>
        <button @click="exportCSV" class="btn-csv" :disabled="filteredSuppliers.length === 0">
          <i class="fas fa-file-csv"></i> Export CSV
        </button>
        <button @click="printLayout" class="btn-print" :disabled="filteredSuppliers.length === 0">
          <i class="fas fa-print"></i> Print
        </button>
      </div>

      <div class="right">
        <input v-model="search" type="text" placeholder="Search name, email, phone, role..." />
      </div>
    </div>

    <!-- KPI Cards -->
    <div class="cards-grid animated fadeInUp">
      <div class="stat-card total">
        <h3>{{ suppliersArray.length }}</h3>
        <p>Total Suppliers</p>
      </div>
      <div class="stat-card workers">
        <h3>{{ countByRole('Worker') }}</h3>
        <p>Farm Workers</p>
      </div>
      <div class="stat-card transporters">
        <h3>{{ countByRole('Transporter') }}</h3>
        <p>Transporters</p>
      </div>
      <div class="stat-card unassigned">
        <h3>{{ unassignedCount }}</h3>
        <p>Unassigned</p>
      </div>
    </div>

    <!-- Filters -->
    <div class="filters">
      <input v-model="roleFilter" type="text" placeholder="Filter by role" />
      <input v-model="unitFilter" type="text" placeholder="Filter by assigned unit" />
      <div class="controls-inline">
        <label>Per page:
          <select v-model.number="perPage">
            <option :value="10">10</option>
            <option :value="20">20</option>
            <option :value="50">50</option>
            <option :value="100">100</option>
          </select>
        </label>

        <label>Sort:
          <select v-model="sortKey">
            <option value="name">Name</option>
            <option value="role">Role</option>
            <option value="email">Email</option>
          </select>
        </label>
        <button @click="toggleSortDir" class="btn-sort">Sort: {{ sortDir.toUpperCase() }}</button>
      </div>
    </div>

    <!-- Table / Loading -->
    <div class="table-wrapper">
      <div v-if="loadingComputed" class="spinner">
        <i class="fas fa-spinner fa-spin"></i> Loading suppliers...
      </div>

      <table v-else-if="paginatedSuppliers.length" class="table">
        <thead>
          <tr>
            <th @click="setSort('name')">Supplier <small class="sort-indicator">{{ headerSortIndicator('name') }}</small></th>
            <th @click="setSort('email')">Email <small class="sort-indicator">{{ headerSortIndicator('email') }}</small></th>
            <th @click="setSort('phone')">Phone <small class="sort-indicator">{{ headerSortIndicator('phone') }}</small></th>
            <th @click="setSort('role')">Role <small class="sort-indicator">{{ headerSortIndicator('role') }}</small></th>
            <th @click="setSort('unitName')">Assigned Unit <small class="sort-indicator">{{ headerSortIndicator('unitName') }}</small></th>
            <th class="text-end">Actions</th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="s in paginatedSuppliers" :key="s.id" class="row-link" @click="goToDetails(s)">
            <td class="cell-user">
              <img :src="s.avatar || defaultAvatar" class="avatar" :alt="s.name" @error="onAvatarError($event)" />
              <div class="user-meta">
                <div class="name">{{ s.name }}</div>
                <div class="small muted">{{ s.title || '' }}</div>
              </div>
            </td>

            <td>
              <a v-if="s.email" :href="`mailto:${s.email}`" @click.stop>{{ s.email }}</a>
              <span v-else class="muted">—</span>
            </td>

            <td>
              <a v-if="s.phone" :href="`tel:${s.phone}`" @click.stop>{{ s.phone }}</a>
              <span v-else class="muted">—</span>
            </td>

            <td>
              <span :class="['badge', roleBadgeClass(s.role)]">{{ s.role || '-' }}</span>
            </td>

            <td>
              <span v-if="s.unitName" class="badge assigned">{{ s.unitName }}</span>
              <span v-else class="badge unassigned">None</span>
            </td>

            <td class="text-end actions-col" @click.stop>
              <button class="btn-small" @click.stop="editSupplier(s)"><i class="fas fa-edit"></i></button>
              <button class="btn-small danger" @click.stop="deleteSupplier(s.id)"><i class="fas fa-trash"></i></button>
            </td>
          </tr>
        </tbody>
      </table>

      <p v-else class="no-data">No suppliers found.</p>

      <!-- Pagination -->
      <div v-if="totalPages > 1" class="pagination">
        <button :disabled="page === 1" @click="goPage(page - 1)">Prev</button>
        <button v-for="p in pageRange" :key="p" @click="goPage(p)" :class="{ active: p === page }">{{ p }}</button>
        <button :disabled="page === totalPages" @click="goPage(page + 1)">Next</button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from "vuex";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export default {
  name: "SuppliersDashboard",
  data() {
    return {
      defaultAvatar: "/img/avatar.png",
      logo: "/img/pacmislogo.png",
      search: "",
      roleFilter: "",
      unitFilter: "",
      page: 1,
      perPage: 20,
      loading: false,
      notifications: [],
      sortKey: "name",
      sortDir: "asc",
    };
  },
  computed: {
    ...mapState("suppliers", ["suppliers", "loading:storeLoading"]),

    loadingComputed() {
      return this.loading || this.storeLoading;
    },

    suppliersArray() {
      return Array.isArray(this.suppliers) ? this.suppliers : Object.values(this.suppliers || {});
    },

    filteredSuppliers() {
      const q = (this.search || "").toLowerCase();
      return this.suppliersArray
        .filter(s => {
          if (q && !`${s.name || ""} ${s.email || ""} ${s.phone || ""} ${s.role || ""}`.toLowerCase().includes(q)) return false;
          if (this.roleFilter && !((s.role || "").toLowerCase().includes(this.roleFilter.toLowerCase()))) return false;
          if (this.unitFilter && !((s.unitName || "").toLowerCase().includes(this.unitFilter.toLowerCase()))) return false;
          return true;
        })
        .sort((a, b) => {
          const va = a[this.sortKey] ?? "";
          const vb = b[this.sortKey] ?? "";
          const na = typeof va === "string" ? va.toLowerCase() : va;
          const nb = typeof vb === "string" ? vb.toLowerCase() : vb;
          if (na < nb) return this.sortDir === "asc" ? -1 : 1;
          if (na > nb) return this.sortDir === "asc" ? 1 : -1;
          return 0;
        });
    },

    paginatedSuppliers() {
      const start = (this.page - 1) * this.perPage;
      return this.filteredSuppliers.slice(start, start + this.perPage);
    },

    totalPages() {
      return Math.ceil(this.filteredSuppliers.length / this.perPage);
    },

    pageRange() {
      const total = this.totalPages;
      if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
      let start = Math.max(1, this.page - 3);
      let end = Math.min(total, this.page + 3);
      if (end - start < 6) start = Math.max(1, end - 6);
      return Array.from({ length: end - start + 1 }, (_, i) => start + i);
    },

    unassignedCount() {
      return this.suppliersArray.filter(s => !s.unitName).length;
    },

    countByRole() {
      return role => this.suppliersArray.filter(s => (s.role || "") === role).length;
    },
  },

  methods: {
    showToast(message, type = "success", duration = 3500) {
      this.notifications.push({ message, type });
      setTimeout(() => this.notifications.shift(), duration);
    },

    refreshSuppliers() {
      this.loading = true;
      return this.$store.dispatch("suppliers/fetchSuppliers")
        .then(() => this.showToast("Suppliers refreshed", "success"))
        .catch(err => this.showToast("Error fetching suppliers: " + (err.message || err), "error"))
        .finally(() => this.loading = false);
    },

    addSupplier() { this.$router.push({ name: "AddSupplier" }); },
    editSupplier(s) { this.$router.push({ name: "EditSupplier", params: { id: s.id } }); },
    deleteSupplier(id) {
      if (!confirm("Delete this supplier?")) return;
      this.$store.dispatch("suppliers/deleteSupplier", id)
        .then(() => this.showToast("Supplier deleted", "success"))
        .catch(err => this.showToast("Delete failed: " + (err.message || err), "error"));
    },
    goToDetails(s) { this.$router.push({ name: "SupplierDetails", params: { id: s.id } }); },
    goPage(p) { if (p >= 1 && p <= this.totalPages) this.page = p; },
    setSort(key) { this.sortKey === key ? this.toggleSortDir() : (this.sortKey = key, this.sortDir = "asc"); },
    toggleSortDir() { this.sortDir = this.sortDir === "asc" ? "desc" : "asc"; },
    headerSortIndicator(key) { return this.sortKey === key ? (this.sortDir === "asc" ? "▲" : "▼") : ""; },
    roleBadgeClass(role) { const r = (role || "").toLowerCase(); return r.includes("worker") ? "role-worker" : r.includes("transporter") ? "role-transporter" : "role-default"; },
    onAvatarError(e) { e.target.src = this.defaultAvatar; },
    async loadImageAsDataURL(url) { if (!url) return null; return fetch(url).then(r => r.blob()).then(blob => new Promise(res => { const reader = new FileReader(); reader.onloadend = () => res(reader.result); reader.readAsDataURL(blob); })); },


    /** EXPORTS ********************************************************/

    async exportPDF() {
      try {
        if (!this.filteredSuppliers.length) {
          this.showToast("No data to export", "error");
          return;
        }
        this.showToast("Preparing PDF...", "loading");

        let logoData = null;
        try {
          logoData = await this.loadImageAsDataURL(this.logo);
        } catch (e) {
          logoData = null;
        }

        const doc = new jsPDF({ orientation: "landscape" });
        const pageWidth = doc.internal.pageSize.getWidth();
        const pageHeight = doc.internal.pageSize.getHeight();
        const title = "Suppliers Report";
        const generated = "Generated: " + new Date().toLocaleString();

        // Build rows (renumbered)
        const rows = this.filteredSuppliers.map((s, i) => [
          i + 1,
          s.name || "-",
          s.email || "-",
          s.phone || "-",
          s.role || "-",
          s.unitName || "-",
        ]);

        autoTable(doc, {
          startY: 36,
          head: [[
            "#",
            "Name",
            "Email",
            "Phone",
            "Role",
            "Assigned Unit"
          ]],
          body: rows,
          styles: { fontSize: 9 },
          headStyles: { fillColor: [230, 230, 230], textColor: 20 },
          theme: "grid",
          margin: { top: 36, bottom: 20 },

          didDrawPage: () => {
            // Header
            if (logoData) {
              try {
                const imgProps = doc.getImageProperties(logoData);
                const imgW = 36;
                const imgH = (imgProps.height * imgW) / imgProps.width;
                doc.addImage(logoData, "PNG", 14, 8, imgW, imgH);
              } catch (e) {
                // ignore
              }
            }
            doc.setFontSize(16);
            doc.text(title, pageWidth / 2, 18, { align: "center" });
            doc.setFontSize(9);
            doc.text(generated, pageWidth - 14, 18, { align: "right" });

            // Footer
            const disclaimer = "Disclaimer: System-generated report. Verify data before use.";
            doc.setFontSize(9);
            doc.text(disclaimer, 14, pageHeight - 10);
            doc.text(`Page ${doc.internal.getNumberOfPages()}`, pageWidth - 14, pageHeight - 10, { align: "right" });
          },

          didParseCell: (cell) => {
            try {
              if (!cell || cell.section !== "body") return;
              if (!cell.styles) cell.styles = {};
              // role column index is 4 (0-based)
              if (cell.column.index === 4) {
                const rowIndex = cell.row.index;
                const rowData = this.filteredSuppliers[rowIndex] || {};
                const r = (rowData.role || "").toLowerCase();
                if (r.includes("worker")) cell.styles.fillColor = [13, 110, 253]; // blue
                else if (r.includes("transporter")) cell.styles.fillColor = [40, 167, 69]; // green
                else cell.styles.fillColor = [245, 245, 245];
                cell.styles.textColor = (cell.styles.fillColor || []).join(",") === "13,110,253" ? 255 : 35;
              }
            } catch (err) {
              console.warn("didParseCell error:", err);
            }
          },
        });

        doc.save(`suppliers_report_${Date.now()}.pdf`);
        this.showToast("PDF exported", "success");
      } catch (err) {
        console.error("PDF export error:", err);
        this.showToast("Error creating PDF: " + (err.message || err), "error");
      }
    },

    async exportExcel() {
      try {
        if (!this.filteredSuppliers.length) {
          this.showToast("No data to export", "error");
          return;
        }
        this.showToast("Preparing Excel...", "loading");

        let XLSX;
        try {
          XLSX = (await import(/* webpackChunkName: "xlsx" */ "xlsx/dist/xlsx.full.min.js")).default || (await import("xlsx")).default;
        } catch (err) {
          try {
            XLSX = (await import("xlsx")).default;
          } catch (e) {
            console.error("xlsx import failed:", e);
            this.showToast("xlsx library not found. Run `npm i xlsx` (or yarn add xlsx).", "error", 7000);
            return;
          }
        }

        const header = ["#", "Name", "Email", "Phone", "Role", "Assigned Unit"];
        const data = this.filteredSuppliers.map((s, i) => [
          i + 1,
          s.name || "",
          s.email || "",
          s.phone || "",
          s.role || "",
          s.unitName || "",
        ]);
        data.unshift(header);

        const ws = XLSX.utils.aoa_to_sheet(data);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Suppliers");

        const wbout = XLSX.write(wb, { bookType: "xlsx", type: "array" });
        const blob = new Blob([wbout], { type: "application/octet-stream" });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `suppliers_${Date.now()}.xlsx`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        window.URL.revokeObjectURL(url);

        this.showToast("Excel ready", "success");
      } catch (err) {
        console.error("Excel error:", err);
        this.showToast("Error creating Excel: " + (err.message || err), "error");
      }
    },

    exportCSV() {
      try {
        if (!this.filteredSuppliers.length) {
          this.showToast("No data to export", "error");
          return;
        }
        const rows = [
          ["#", "Name", "Email", "Phone", "Role", "Assigned Unit"],
          ...this.filteredSuppliers.map((s, i) => [
            i + 1,
            `"${(s.name || "").replace(/"/g, '""')}"`,
            s.email || "",
            s.phone || "",
            s.role || "",
            s.unitName || "",
          ]),
        ];
        const csv = rows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `suppliers_${Date.now()}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        this.showToast("CSV ready", "success");
      } catch (err) {
        console.error("CSV export error:", err);
        this.showToast("Error creating CSV: " + (err.message || err), "error");
      }
    },

    printLayout() {
      const rowsHtml = this.filteredSuppliers.map((s, i) => `
        <tr>
          <td style="padding:6px;border:1px solid #ddd;">${i + 1}</td>
          <td style="padding:6px;border:1px solid #ddd;">${s.name || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;">${s.email || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;">${s.phone || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;">${s.role || ""}</td>
          <td style="padding:6px;border:1px solid #ddd;">${s.unitName || ""}</td>
        </tr>
      `).join("");

      const html = `
        <html>
        <head>
          <title>Suppliers Report</title>
          <style>
            body { font-family: Arial, Helvetica, sans-serif; margin:20px; color:#222; }
            .header { display:flex; align-items:center; gap:12px; margin-bottom:12px; }
            .header img { height:48px; }
            table { width:100%; border-collapse:collapse; }
            th { background:#f6f7fb; padding:8px; border:1px solid #eaeaea; text-align:left; }
            td { padding:6px; border:1px solid #eaeaea; }
            .disclaimer { margin-top:12px; color:#666; font-size:13px; }
          </style>
        </head>
        <body>
          <div class="header">
            <img src="${this.logo}" alt="logo" />
            <div>
              <h2 style="margin:0;">Suppliers Report</h2>
              <div style="color:#666;">Generated: ${new Date().toLocaleString()}</div>
            </div>
          </div>

          <table>
            <thead><tr>
              <th>#</th><th>Name</th><th>Email</th><th>Phone</th><th>Role</th><th>Assigned Unit</th>
            </tr></thead>
            <tbody>
              ${rowsHtml}
            </tbody>
          </table>

          <div class="disclaimer">Disclaimer: System-generated report. Verify data before use.</div>
        </body>
        </html>
      `;
      const w = window.open("", "_blank");
      if (!w) {
        this.showToast("Popup blocked. Allow popups to use Print.", "error");
        return;
      }
      w.document.write(html);
      w.document.close();
      setTimeout(() => w.print(), 600);
    },
  },

  mounted() {
    this.sortKey = "name";
    this.sortDir = "asc";
    this.refreshSuppliers(); // always load fresh data on mount
  },

  watch: {
    search() { this.page = 1; },
    roleFilter() { this.page = 1; },
    unitFilter() { this.page = 1; },
    perPage() { this.page = 1; },
  },
};
</script>

<style scoped>
/* wrapper */
.suppliers-wrapper { padding: 18px; background: #f4f6f9; }

/* notifications */
.notifications-wrapper { position: fixed; top: 20px; right: 20px; display:flex; flex-direction:column; gap:10px; z-index:9999; }
.notification { padding: 10px 14px; border-radius:8px; color:#fff; min-width:200px; box-shadow:0 4px 12px rgba(0,0,0,0.08); }
.notification.success { background:#28a745; } .notification.error { background:#dc3545; } .notification.loading { background:#0d6efd; }

/* toolbar */
.toolbar { display:flex; justify-content:space-between; align-items:center; gap:12px; margin-bottom:14px; }
.toolbar .left { display:flex; gap:8px; align-items:center; }
.toolbar .right input { padding:8px 12px; border-radius:8px; border:1px solid #dcdcdc; min-width:260px; }

/* buttons */
.btn-gradient, .btn-pdf, .btn-excel, .btn-csv, .btn-print, .btn-refresh {
  padding:8px 12px; border-radius:8px; border:none; color:#fff; cursor:pointer; font-weight:600;
}
.btn-gradient { background: linear-gradient(45deg,#6a11cb,#2575fc); }
.btn-refresh { background:#20c997; }
.btn-pdf { background:#d63333; }
.btn-excel { background:#107c10; }
.btn-csv { background:#0a58ca; }
.btn-print { background:#6c757d; }

.btn-sort { padding:6px 10px; border-radius:6px; background:#343a40; color:#fff; border:none; }

/* cards */
.cards-grid { display:grid; grid-template-columns: repeat(4, 1fr); gap:12px; margin-bottom:14px; }
.stat-card { background:#fff; padding:14px; border-radius:12px; text-align:center; box-shadow:0 6px 18px rgba(0,0,0,0.04); }
.stat-card h3 { margin:0; font-size:1.25rem; }
.stat-card p { margin:6px 0 0; color:#6b6b6b; }

/* filters */
.filters { display:flex; gap:12px; align-items:center; margin-bottom:12px; flex-wrap:wrap; }
.filters input { padding:8px 10px; border-radius:8px; border:1px solid #e0e0e0; }

/* table */
.table-wrapper { background:#fff; border-radius:12px; padding:8px; box-shadow:0 8px 24px rgba(27,31,35,0.03); }
.table { width:100%; border-collapse:collapse; }
.table thead th { background:#eef2f7; padding:12px 10px; text-align:left; cursor:pointer; }
.table tbody td { padding:12px 10px; border-bottom:1px solid #f1f3f5; vertical-align:middle; }

/* user cell */
.cell-user { display:flex; align-items:center; gap:12px; }
.avatar { width:44px; height:44px; border-radius:50%; object-fit:cover; border:2px solid #fff; box-shadow:0 2px 6px rgba(0,0,0,0.06); }
.user-meta .name { font-weight:600; }
.user-meta .small { font-size:0.85rem; color:#6b6b6b; }

/* badges */
.badge { padding:6px 10px; border-radius:8px; color:#fff; font-weight:600; }
.role-worker { background:#0d6efd; } /* blue */
.role-transporter { background:#198754; } /* green */
.role-default { background:#6c757d; } /* gray */
.assigned { background:#2575fc; }
.unassigned { background:#f39c12; color:#000; }

/* actions */
.actions-col { white-space:nowrap; }
.btn-small { padding:8px 10px; border-radius:8px; margin-left:6px; border:none; cursor:pointer; background:#e9ecf2; color:#222; }
.btn-small.danger { background:#ff6b6b; color:#fff; }

/* pagination */
.pagination { display:flex; gap:8px; margin-top:12px; justify-content:center; align-items:center; }
.pagination button { padding:6px 10px; border-radius:6px; border:1px solid #ddd; background:#fff; cursor:pointer; }
.pagination button.active { background:#0d6efd; color:#fff; border-color:#0d6efd; }

/* no-data & spinner */
.no-data { padding:18px; text-align:center; color:#666; }
.spinner { text-align:center; padding:20px; color:#6b6b6b; }

/* responsive */
@media (max-width: 900px) {
  .cards-grid { grid-template-columns: repeat(2, 1fr); }
  .toolbar .right input { min-width: 140px; }
  .table thead th:nth-child(3), .table thead th:nth-child(5) { display:none; }
  .table tbody td:nth-child(3), .table tbody td:nth-child(5) { display:none; }
}
</style>
